import $ from "dabbyjs/src/core/dabby/dabby.js";
import "dabbyjs/src/core/each/each.js";
import "dabbyjs/src/events/on/on.js";
import "dabbyjs/src/traversal/find/find.js";
import "dabbyjs/src/traversal/closest/closest.js";

const DAY_MS = 86400000;

// Roadmap Gantt timeline chart: hover tooltips for milestone markers (data-milestone-*) and,
// when the chart is rendered with data-editable (admin view, not the public share link), drag
// to reschedule a bar and drag its edges to resize — persisted via a controller-driven AJAX
// action (POST + Accept: application/json).
//
// Rescheduling is the only write here. Creating and editing projects is the Roadmap CMS's job;
// the inline popups that used to do it from this chart have been removed.
export default obj => {
	if (!obj.length) {
		return;
	}

	obj.each(function () {
		setupTooltips(this);
		if (this.hasAttribute("data-editable")) {
			setupDragResize(this);
		}
		// fit is a question of rendered pixels, so it is re-asked whenever the chart's width changes
		// (also covers the first layout, and a tab that was hidden at load reporting zero widths)
		const root = this;
		setupPhaseExpanders(root);
		new ResizeObserver(() => {
			placeLabels(root);
			offsetStickyHeader(root);
		}).observe(root);
	});
};

// The report's tab bar is itself sticky at the top of the page, so the months header has to stick
// just below it rather than underneath it. Its height depends on how the tabs wrap, so it's measured.
function offsetStickyHeader(root) {
	let offset = 0;
	for (let el = root.parentElement; el; el = el.parentElement) {
		for (const child of el.children) {
			if (child.contains(root) || getComputedStyle(child).position !== "sticky") {
				continue;
			}
			offset = Math.max(offset, child.getBoundingClientRect().height + (parseFloat(getComputedStyle(child).top) || 0));
		}
	}
	root.style.setProperty("--gantt-sticky-top", offset + "px");
}

// The arrow on a phased project's name shows or hides its phase rows; "Expand all phases" does every
// project at once, and stays ticked only while every project is open. Everything starts collapsed,
// so the page opens as the overview.
function setupPhaseExpanders(root) {
	const buttons = Array.from(root.querySelectorAll(".roadmap-gantt__expand")),
		all = root.querySelector("[data-gantt-expand-all]");

	const setOpen = (button, open) => {
		const block = root.querySelector("#" + CSS.escape(button.getAttribute("aria-controls")));
		button.setAttribute("aria-expanded", String(open));
		if (block) {
			block.hidden = !open;
			if (open) {
				// hidden rows measure as zero width, so their labels can only be placed once shown
				placePhaseLabels(block);
			}
		}
	};
	const syncAll = () => {
		if (all) {
			all.checked = buttons.every(b => b.getAttribute("aria-expanded") === "true");
		}
	};

	buttons.forEach(button => button.addEventListener("click", () => {
		setOpen(button, button.getAttribute("aria-expanded") !== "true");
		syncAll();
	}));
	if (all) {
		all.addEventListener("change", () => buttons.forEach(button => setOpen(button, all.checked)));
	}
}

// A phase name sits inside its phase when it fits; otherwise after it, or before it when only that
// side has the room (each phase has its row to itself, so only the chart edges limit it).
function placePhaseLabels(scope) {
	scope.querySelectorAll(".roadmap-gantt__phase-row").forEach(row => {
		const phase = row.querySelector(".roadmap-gantt__phase"),
			label = phase ? phase.querySelector(".roadmap-gantt__phase-label") : null;
		if (!label) {
			return;
		}
		phase.classList.remove("roadmap-gantt__phase--label-after", "roadmap-gantt__phase--label-before");
		if (label.scrollWidth <= label.clientWidth) {
			return;
		}
		const rowRect = row.getBoundingClientRect(),
			rect = phase.getBoundingClientRect(),
			after = rowRect.right - rect.right,
			before = rect.left - rowRect.left;
		phase.classList.add(after >= label.scrollWidth + 8 || after >= before
			? "roadmap-gantt__phase--label-after"
			: "roadmap-gantt__phase--label-before");
	});
}

// Sizes each project name (drawn above its bar) to the room actually free in its lane, and places the
// labels of any open phase rows. A name runs right from its bar's start up
// to the next bar in the lane (lanes are packed) or the chart edge; when that's too short and there
// is more room leftwards — a bar near the right edge — it end-aligns to the bar instead.
function placeLabels(root) {
	const gap = 8;
	root.querySelectorAll(".roadmap-gantt__lane").forEach(lane => {
		const laneRect = lane.getBoundingClientRect(),
			placed = Array.from(lane.querySelectorAll(".roadmap-gantt__bar"))
				.map(bar => ({ bar, rect: bar.getBoundingClientRect() }))
				.sort((a, b) => a.rect.left - b.rect.left);

		// right edge of whatever is already drawn in this lane's name row, so an end-aligned name
		// can't run back over the previous bar's
		let occupied = laneRect.left;
		placed.forEach(({ bar, rect }, i) => {
			const label = bar.querySelector(".roadmap-gantt__bar-label");
			if (!label) {
				return;
			}
			bar.classList.remove("roadmap-gantt__bar--label-end");
			label.style.maxWidth = "";

			const natural = label.scrollWidth,
				rightRoom = (placed[i + 1] ? placed[i + 1].rect.left : laneRect.right) - rect.left - gap,
				leftRoom = rect.right - occupied - gap,
				endAlign = natural > rightRoom && leftRoom > rightRoom,
				room = Math.max(0, endAlign ? leftRoom : rightRoom);

			bar.classList.toggle("roadmap-gantt__bar--label-end", endAlign);
			label.style.maxWidth = room + "px";
			occupied = Math.max(rect.right, endAlign ? rect.right : rect.left + Math.min(natural, room));
		});
	});

	root.querySelectorAll(".roadmap-gantt__phase-rows:not([hidden])").forEach(placePhaseLabels);
}

function setupTooltips(root) {
	const tooltip = root.querySelector(".roadmap-gantt__tooltip");
	if (!tooltip) {
		return;
	}
	const tooltipName    = tooltip.querySelector(".roadmap-gantt__tooltip-name"),
		tooltipProject = tooltip.querySelector(".roadmap-gantt__tooltip-project"),
		tooltipDate    = tooltip.querySelector(".roadmap-gantt__tooltip-date"),
		tooltipStatus  = tooltip.querySelector(".roadmap-gantt__tooltip-status");

	const positionTooltip = e => {
		const rect = root.getBoundingClientRect(),
			tw = tooltip.offsetWidth,
			th = tooltip.offsetHeight;
		let x = e.clientX - rect.left + 12,
			y = e.clientY - rect.top - th - 12;
		if (x + tw > rect.width) { x = e.clientX - rect.left - tw - 12; }
		if (y < 0) { y = e.clientY - rect.top + 12; }
		tooltip.style.left = x + "px";
		tooltip.style.top  = y + "px";
	};

	// milestones and phase segments share the one tooltip; each carries its text as data-<kind>-*
	$(root).find(".roadmap-gantt__milestone, .roadmap-gantt__phase").each(function () {
		const kind = this.classList.contains("roadmap-gantt__phase") ? "phase" : "milestone";
		$(this).on("mouseenter", e => {
			tooltipName.textContent    = this.dataset[kind + "Name"] || "";
			tooltipProject.textContent = this.dataset[kind + "Project"] || "";
			tooltipDate.textContent    = this.dataset[kind + "Date"] || "";
			tooltipStatus.textContent  = this.dataset[kind + "Status"] || "";
			tooltip.classList.add("roadmap-gantt__tooltip--visible");
			tooltip.setAttribute("aria-hidden", "false");
			positionTooltip(e);
		});
		$(this).on("mousemove", positionTooltip);
		$(this).on("mouseleave", () => {
			tooltip.classList.remove("roadmap-gantt__tooltip--visible");
			tooltip.setAttribute("aria-hidden", "true");
		});
	});
}

// a project's phases live in their own rows beneath its lane, not inside the bar
function phasesOf(bar) {
	const root = bar.closest("[data-gantt]"),
		block = root ? root.querySelector('[data-phases-for="' + bar.dataset.projectId + '"]') : null;
	return block ? Array.from(block.querySelectorAll(".roadmap-gantt__phase")) : [];
}

// Re-places a bar's milestone markers and phase rows for new bar dates. Milestones are positioned
// as a percentage of the bar's own width, so a move needs nothing (they ride along), but a resize
// would otherwise stretch them across dates they don't fall on. Phase rows sit on the chart's own
// axis; they are cut to the bar and hidden when nothing of them is inside it, matching how the
// server renders them.
function layoutBarContents(bar, barStart, barEnd) {
	// a bar running past the chart's range is drawn cut at the edge, so its contents are laid out
	// against the visible part of it — the same clamping the server applies
	const root = bar.closest("[data-gantt]"),
		rangeStart = new Date(root.dataset.rangeStart + "T00:00:00Z").getTime(),
		rangeEnd = rangeStart + parseInt(root.dataset.rangeDays, 10) * DAY_MS,
		startMs = Math.max(barStart, rangeStart),
		endMs = Math.min(barEnd, rangeEnd),
		span = Math.max(DAY_MS, endMs - startMs),
		pct = ms => ((ms - startMs) / span) * 100,
		chartPct = ms => ((ms - rangeStart) / (rangeEnd - rangeStart)) * 100,
		parse = iso => new Date(iso + "T00:00:00Z").getTime();

	phasesOf(bar).forEach(phase => {
		const from = Math.max(parse(phase.dataset.start), startMs),
			to = Math.min(parse(phase.dataset.end), endMs);
		phase.hidden = from > to;
		if (from <= to) {
			phase.style.left = chartPct(from) + "%";
			phase.style.width = Math.min(100 - chartPct(from), Math.max(0.3, chartPct(to) - chartPct(from))) + "%";
		}
	});
	bar.querySelectorAll(".roadmap-gantt__milestone").forEach(milestone => {
		const at = parse(milestone.dataset.start);
		milestone.hidden = at < startMs || at > endMs;
		milestone.style.left = Math.max(0, Math.min(100, pct(at))) + "%";
	});
}

function setupDragResize(root) {
	const rangeStart = new Date(root.dataset.rangeStart + "T00:00:00Z").getTime(),
		rangeDays = parseInt(root.dataset.rangeDays, 10),
		dragTooltip = root.querySelector(".roadmap-gantt__drag-tooltip");

	const dateToPct = ms => Math.max(0, Math.min(100, ((ms - rangeStart) / DAY_MS / rangeDays) * 100));
	const pxToDays = (px, trackWidth) => Math.round((px / trackWidth) * rangeDays);
	const toIso = ms => new Date(ms).toISOString().slice(0, 10);
	const formatDate = ms => new Date(ms).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

	const positionDragTooltip = (e, text) => {
		if (!dragTooltip) {
			return;
		}
		dragTooltip.textContent = text;
		const rect = root.getBoundingClientRect(),
			tw = dragTooltip.offsetWidth || 160;
		let x = e.clientX - rect.left - tw / 2,
			y = e.clientY - rect.top - dragTooltip.offsetHeight - 16;
		x = Math.max(0, Math.min(rect.width - tw, x));
		dragTooltip.style.left = x + "px";
		dragTooltip.style.top  = Math.max(0, y) + "px";
		dragTooltip.classList.add("roadmap-gantt__drag-tooltip--visible");
		dragTooltip.setAttribute("aria-hidden", "false");
	};

	const hideDragTooltip = () => {
		if (!dragTooltip) {
			return;
		}
		dragTooltip.classList.remove("roadmap-gantt__drag-tooltip--visible");
		dragTooltip.setAttribute("aria-hidden", "true");
	};

	$(root).find(".roadmap-gantt__bar").each(function () {
		const bar = this;
		let mode = null, // "move" | "resize-start" | "resize-end"
			dragged = false, // true once real movement has happened, so a click doesn't persist a no-op
			startX = 0,
			track = null,
			trackWidth = 0,
			startWidthPx = 0,
			origStart = 0,
			origEnd = 0;

		const onPointerDown = e => {
			track = bar.closest(".roadmap-gantt__lane");
			// the expand arrow sits inside the bar's name; pressing it must not pick the bar up (pointer
			// capture would also swallow the click)
			if (!track || e.target.closest(".roadmap-gantt__expand")) {
				track = null;
				return;
			}
			trackWidth = track.getBoundingClientRect().width;
			startX = e.clientX;
			startWidthPx = bar.offsetWidth;
			dragged = false;
			origStart = new Date(bar.dataset.start + "T00:00:00Z").getTime();
			origEnd   = new Date(bar.dataset.end + "T00:00:00Z").getTime();

			const edgeZone = Math.min(10, startWidthPx / 4),
				offsetX = e.clientX - bar.getBoundingClientRect().left;
			if (offsetX <= edgeZone) {
				mode = "resize-start";
			} else if (offsetX >= startWidthPx - edgeZone) {
				mode = "resize-end";
			} else {
				mode = "move";
			}

			bar.setPointerCapture(e.pointerId);
		};

		const onPointerMove = e => {
			if (!mode) {
				return;
			}
			const deltaX = e.clientX - startX;
			if (Math.abs(deltaX) < 4) {
				return; // below the drag threshold — treat as not-yet-dragging
			}
			if (!dragged) {
				dragged = true;
				bar.classList.add("roadmap-gantt__bar--dragging");
			}

			const deltaDays = pxToDays(deltaX, trackWidth);

			if (mode === "move") {
				const newStart = origStart + deltaDays * DAY_MS,
					newEnd = origEnd + deltaDays * DAY_MS;
				bar.style.left = dateToPct(newStart) + "%";
				positionDragTooltip(e, formatDate(newStart) + " – " + formatDate(newEnd));
			} else if (mode === "resize-start") {
				const newStart = Math.min(origStart + deltaDays * DAY_MS, origEnd - DAY_MS);
				bar.style.left = dateToPct(newStart) + "%";
				bar.style.width = Math.max(0.5, dateToPct(origEnd) - dateToPct(newStart)) + "%";
				layoutBarContents(bar, newStart, origEnd);
				positionDragTooltip(e, formatDate(newStart) + " – " + formatDate(origEnd));
			} else if (mode === "resize-end") {
				const newEnd = Math.max(origEnd + deltaDays * DAY_MS, origStart + DAY_MS);
				bar.style.width = Math.max(0.5, dateToPct(newEnd) - dateToPct(origStart)) + "%";
				layoutBarContents(bar, origStart, newEnd);
				positionDragTooltip(e, formatDate(origStart) + " – " + formatDate(newEnd));
			}
		};

		const onPointerUp = e => {
			if (!mode) {
				return;
			}
			const wasDragged = dragged,
				endedMode = mode;
			mode = null;
			dragged = false;
			bar.classList.remove("roadmap-gantt__bar--dragging");
			hideDragTooltip();

			if (!wasDragged) {
				return; // a click, not a drag — nothing to persist, and the bar opens nothing
			}

			const deltaX = e.clientX - startX,
				deltaDays = pxToDays(deltaX, trackWidth);
			let newStart = origStart, newEnd = origEnd;

			if (endedMode === "move") {
				newStart = origStart + deltaDays * DAY_MS;
				newEnd   = origEnd + deltaDays * DAY_MS;
			} else if (endedMode === "resize-start") {
				newStart = Math.min(origStart + deltaDays * DAY_MS, origEnd - DAY_MS);
			} else if (endedMode === "resize-end") {
				newEnd = Math.max(origEnd + deltaDays * DAY_MS, origStart + DAY_MS);
			}

			// Which lane row the pointer was released over. A new lane is only sent when the bar
			// actually CHANGED row — a non-zero projectLane pins the bar there and excludes it from
			// auto-packing, so sending the current row on every horizontal-only reschedule would
			// silently pin every bar a user ever drags, leaving the portfolio one-project-per-lane
			// with gaps that can no longer close up. Dragging within the same row keeps whatever pin
			// the bar already had (0 = still auto-packed), so a deliberate pin survives a reschedule.
			const lanes = Array.from(root.querySelectorAll(".roadmap-gantt__lane")),
				overLane = lanes.find(l => {
					// a lane's name row is its top margin — dropping onto a project name counts as
					// dropping onto that lane, or half the board would be dead space
					const r = l.getBoundingClientRect(),
						nameRow = parseFloat(getComputedStyle(l).marginTop) || 0;
					return e.clientY >= r.top - nameRow && e.clientY <= r.bottom;
				}),
				lane = (overLane && overLane !== track)
					? parseInt(overLane.dataset.lane, 10) + 1
					: parseInt(bar.dataset.lane, 10) || 0;

			const startIso = toIso(newStart), endIso = toIso(newEnd);
			if (startIso === bar.dataset.start && endIso === bar.dataset.end && overLane === track) {
				return; // no actual change — snap back to the server-rendered position, nothing to save
			}

			persistSchedule(bar, startIso, endIso, lane);
		};

		bar.addEventListener("pointerdown", onPointerDown);
		bar.addEventListener("pointermove", onPointerMove);
		bar.addEventListener("pointerup", onPointerUp);
	});
}

function persistSchedule(bar, start, end, lane) {
	const prevLeft = bar.style.left, prevWidth = bar.style.width,
		root = bar.closest("[data-gantt]"),
		// the security layer wipes $_POST on any request missing its csrf token — read it from
		// the chart's own hidden schedule form, which exists solely to have a token minted into
		// it. The token is per-URL (getCsrfPageToken($action)), so we must post to that SAME
		// action URL, not location.href — they differ once the page carries filter params
		form = root ? root.querySelector(".roadmap-gantt__schedule-form") : null,
		token = form ? form.querySelector('input[name="csrftoken"]') : null,
		url = form ? form.getAttribute("action") : null,
		params = { action: "schedule", project: bar.dataset.projectId, start, end, lane, bttrp: "" };
	if (token) {
		params.csrftoken = token.value;
	}

	fetch(url || location.href, {
		method: "POST",
		headers: { accept: "application/json" },
		body: new URLSearchParams(params),
	})
		.then(response => response.json())
		.then(json => {
			if (json.error) {
				bar.style.left = prevLeft;
				bar.style.width = prevWidth;
				restoreBarContents(bar);
			} else {
				// a move (both ends shifted equally) carried the phases with it on the server too
				// (roadmapActions::schedule), so their dates here shift by the same amount
				const shift = isoToMs(start) - isoToMs(bar.dataset.start);
				if (shift !== 0 && shift === isoToMs(end) - isoToMs(bar.dataset.end)) {
					phasesOf(bar).forEach(phase => {
						phase.dataset.start = new Date(isoToMs(phase.dataset.start) + shift).toISOString().slice(0, 10);
						phase.dataset.end = new Date(isoToMs(phase.dataset.end) + shift).toISOString().slice(0, 10);
					});
				}
				bar.dataset.start = start;
				bar.dataset.end = end;
				// milestones are not moved by a reschedule, so after a move they belong back at
				// their real dates rather than riding along with the bar
				layoutBarContents(bar, isoToMs(start), isoToMs(end));
				// keep the pin in sync, so a second drag of the same bar sends the lane the server
				// actually stored rather than the one rendered on page load
				bar.dataset.lane = String(lane);
			}
			showFlash(bar, json);
			placeLabels(root);
		})
		.catch(() => {
			bar.style.left = prevLeft;
			bar.style.width = prevWidth;
			restoreBarContents(bar);
			showFlash(bar, { error: true, msg: fallbackMsg(bar) });
			placeLabels(root);
		});
}

const isoToMs = iso => new Date(iso + "T00:00:00Z").getTime();

// back to the bar's saved dates, after a failed save has snapped the bar itself back
function restoreBarContents(bar) {
	layoutBarContents(bar, isoToMs(bar.dataset.start), isoToMs(bar.dataset.end));
}

// message for when the response wasn't JSON at all, so carries no server-side msg — sourced from
// the chart root so it stays translatable (see data-error-msg in charts/gantt-timeline.php)
function fallbackMsg(el) {
	const root = el.closest("[data-gantt]");
	return (root && root.dataset.errorMsg) || "Could not save.";
}

// Shows a msg flash for an action. When the action came from a popup form the message belongs
// inside that dialog — the chart root sits behind the popup's backdrop, so a flash there is
// invisible while it's open. Falls back to the chart root for the drag action, which has no popup.
function showFlash(el, json) {
	const panel = el.closest(".hub__content-inner"),
		target = panel || el.closest("[data-gantt]");
	if (!target) {
		return;
	}
	// replace rather than stack, so repeated failed submits don't pile messages up in the dialog
	const existing = target.querySelector(":scope > .msg");
	if (existing) {
		existing.remove();
	}
	const cls = "msg",
		msg = document.createElement("div");
	msg.className = cls + " " + cls + "--" + (json.error ? "error" : "success");
	msg.textContent = json.msg;
	target.prepend(msg);
	// a message in the dialog stays until the next submit — it's the only feedback the user has
	// there, and the dialog can outlive a timeout; the chart-root one still self-clears
	if (!panel) {
		setTimeout(() => msg.remove(), 4000);
	}
}
